import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  MapPin, Battery, Clock, Shield, Zap, Users, MapPinned, Percent,
  Sun, Leaf, Heart, Award, Building2, UserCheck, Globe, ArrowRight
} from 'lucide-react';
import StationGrid from '../components/stations/StationGrid';

const Stations = () => {
  const features = [
    {
      icon: <Sun className="w-6 h-6 text-primary-600" />,
      title: "100% Solaire",
      description: "Stations alimentées par énergie solaire pour une recharge écologique"
    },
    {
      icon: <Users className="w-6 h-6 text-primary-600" />,
      title: "Gérées par des Femmes",
      description: "Création d'emplois et autonomisation des femmes"
    },
    {
      icon: <Clock className="w-6 h-6 text-primary-600" />,
      title: "24/7 Disponible",
      description: "Accès aux stations jour et nuit avec assistance"
    },
    {
      icon: <Shield className="w-6 h-6 text-primary-600" />,
      title: "100% Sécurisé",
      description: "Surveillance vidéo et personnel formé"
    }
  ];

  const stats = [
    {
      icon: <MapPinned className="w-8 h-8 text-primary-600" />,
      value: "50+",
      label: "Stations",
      description: "à travers l'Afrique"
    },
    {
      icon: <UserCheck className="w-8 h-8 text-primary-600" />,
      value: "150+",
      label: "Femmes Employées",
      description: "formées et autonomes"
    },
    {
      icon: <Sun className="w-8 h-8 text-primary-600" />,
      value: "100%",
      label: "Énergie Solaire",
      description: "impact environnemental"
    },
    {
      icon: <Percent className="w-8 h-8 text-primary-600" />,
      value: "99.9%",
      label: "Disponibilité",
      description: "du réseau"
    }
  ];

  const benefits = [
    {
      icon: <Leaf className="w-12 h-12 text-accent-300" />,
      title: "Impact Environnemental",
      description: "Réduction des émissions de CO2 grâce à l'énergie solaire",
      stats: "500+ tonnes de CO2 évitées"
    },
    {
      icon: <Heart className="w-12 h-12 text-accent-300" />,
      title: "Impact Social",
      description: "Création d'emplois durables pour les femmes",
      stats: "150+ emplois créés"
    },
    {
      icon: <Award className="w-12 h-12 text-accent-300" />,
      title: "Formation Continue",
      description: "Programme de formation technique et gestion",
      stats: "1000+ heures de formation"
    },
    {
      icon: <Building2 className="w-12 h-12 text-accent-300" />,
      title: "Développement Local",
      description: "Partenariats avec les communautés locales",
      stats: "20+ communes partenaires"
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Stations de Recharge | Proxy Emobility</title>
        <meta name="description" content="Découvrez notre réseau de stations de recharge Proxy Power alimentées à 100% par énergie solaire et gérées par des femmes à travers l'Afrique." />
        <meta name="keywords" content="stations de recharge, Proxy Power, énergie solaire, mobilité électrique, Afrique, emploi féminin, développement durable" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/stations" />
        
        {/* Schema.org structured data for LocalBusiness */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Proxy Power - Stations de Recharge",
              "image": "https://proxyemobility.netlify.app/StationSolar.png",
              "description": "Réseau de stations de recharge pour motos électriques alimentées à 100% par énergie solaire et gérées par des femmes.",
              "openingHours": "Mo-Su 00:00-23:59",
              "telephone": "+221 77 123 45 67",
              "priceRange": "$$",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "Sénégal"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": "14.6937",
                "longitude": "-17.4441"
              },
              "sameAs": [
                "https://www.facebook.com/proxyemobility",
                "https://www.twitter.com/proxyemobility",
                "https://www.instagram.com/proxyemobility",
                "https://www.linkedin.com/company/proxyemobility"
              ]
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 to-primary-900 text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
              Stations de Recharge Proxy Power
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Un réseau de stations solaires gérées par des femmes, alliant innovation technologique et impact social pour une mobilité durable en Afrique.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center transform hover:scale-105 transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4 shadow-lg">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-lg font-semibold text-primary-600 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 border border-primary-100">
                <div className="flex-shrink-0 mr-4">
                  <div className="p-3 bg-primary-50 rounded-xl">
                    {feature.icon}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solar Station Showcase */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                Stations Solaires Innovantes
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Nos stations Proxy Power sont alimentées à 100% par l'énergie solaire, 
                réduisant significativement notre empreinte carbone tout en offrant une 
                solution de recharge fiable et accessible.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-primary-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                  <div className="text-2xl font-bold text-primary-600 mb-1">100%</div>
                  <div className="text-gray-600">Énergie Solaire</div>
                </div>
                <div className="bg-primary-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                  <div className="text-2xl font-bold text-primary-600 mb-1">24/7</div>
                  <div className="text-gray-600">Disponibilité</div>
                </div>
                <div className="bg-primary-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                  <div className="text-2xl font-bold text-primary-600 mb-1">4</div>
                  <div className="text-gray-600">Points de Charge</div>
                </div>
                <div className="bg-primary-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                  <div className="text-2xl font-bold text-primary-600 mb-1">30min</div>
                  <div className="text-gray-600">Temps de Charge</div>
                </div>
              </div>
            </div>
            <div className="relative group">
              <img
                src="/StationSolar.png"
                alt="Station Proxy Power"
                className="rounded-xl shadow-2xl transform group-hover:scale-105 transition-all duration-500"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs transform group-hover:translate-x-2 group-hover:translate-y-2 transition-all duration-300">
                <div className="flex items-center mb-3">
                  <Sun className="text-primary-500 mr-2" size={20} />
                  <span className="font-medium text-gray-900">Impact Environnemental</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Chaque station permet d'éviter l'émission de plus de 10 tonnes de CO2 par an
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Women Empowerment Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
              Autonomisation des Femmes
            </h2>
            <p className="text-lg text-gray-600">
              Notre réseau de stations est géré par des femmes formées et qualifiées, 
              créant des opportunités d'emploi durables et favorisant l'inclusion dans 
              le secteur de la mobilité électrique.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl transform hover:-translate-y-2">
                <div className="mb-6 transform hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 mb-4">{benefit.description}</p>
                <div className="text-primary-600 font-semibold">{benefit.stats}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map and Stations List */}
      <section id="find-station" className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Trouvez une Station</h2>
            <p className="text-lg text-gray-600">
              Localisez la station Proxy Power la plus proche et rechargez votre moto électrique 
              en toute simplicité, tout en soutenant l'emploi local et l'environnement.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-all duration-300">
              <img 
                src="/trouvetastation.png" 
                alt="Carte des stations"
                className="w-full h-[600px] object-cover"
              />
            </div>

            <div className="h-[600px] overflow-y-auto bg-white rounded-xl shadow-xl">
              <StationGrid />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary-800 to-primary-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Rejoignez le Mouvement
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Participez à la révolution de la mobilité électrique en Afrique. 
              Ensemble, créons un avenir plus durable et inclusif.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-900 font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Devenir Partenaire
                <ArrowRight size={20} className="ml-2 inline-block" />
              </a>
              <a 
                href="/products" 
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30"
              >
                Voir Nos Produits
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Stations;